import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, Mail, Phone, MapPin, ShoppingBag, LogOut, Edit2, CheckCircle, Save, X, ChevronRight, Package, LayoutDashboard } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ADMIN_EMAIL = "contact@atelierdumac.fr";
const inputCls = "w-full bg-[#1A1A1E] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FF5C00] transition-colors text-sm";

export default function ProfilePage() {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [orders, setOrders] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveOk, setSaveOk] = useState(false);

  useEffect(() => {
    document.title = "Mon compte — Atelier du Mac";
    if (!user) { navigate("/connexion"); return; }
    setForm({ prenom: user.prenom || "", nom: user.nom || "", telephone: user.telephone || "", adresse_postale: user.adresse_postale || "" });
    fetchOrders();
  }, [user]);

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${BACKEND}/api/auth/orders`, { credentials: "include" });
      if (res.ok) setOrders(await res.json());
    } catch {}
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND}/api/auth/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (res.ok) {
        const updated = await res.json();
        setUser(updated);
        setEditing(false);
        setSaveOk(true);
        setTimeout(() => setSaveOk(false), 2000);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (!user) return null;

  return (
    <div className="bg-[#0A0A0C] min-h-screen">
      <section className="pt-32 pb-8 max-w-4xl mx-auto px-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-white/60 mb-8 transition-colors">
          <ChevronRight className="w-3 h-3 rotate-180" /> Retour à l'accueil
        </Link>
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-[#FF5C00]/10 border border-[#FF5C00]/20 flex items-center justify-center overflow-hidden">
              {user.picture
                ? <img src={user.picture} alt={user.prenom} className="w-full h-full object-cover" />
                : <User className="w-8 h-8 text-[#FF5C00]" />}
            </div>
            <div>
              <h1 className="text-2xl font-black text-white">{user.prenom} {user.nom}</h1>
              <p className="text-sm text-white/40">{user.email}</p>
            </div>
          </div>
          <button data-testid="logout-btn" onClick={handleLogout}
            className="flex items-center gap-2 border border-white/15 hover:border-white/30 text-white/60 hover:text-white text-sm font-semibold px-4 py-2.5 rounded-full transition-colors">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
        {user.email === ADMIN_EMAIL && (
          <Link to="/admin" data-testid="admin-link"
            className="inline-flex items-center gap-2 mb-2 text-xs font-semibold text-[#FF5C00] bg-[#FF5C00]/10 border border-[#FF5C00]/20 px-4 py-2 rounded-full hover:bg-[#FF5C00]/15 transition-colors">
            <LayoutDashboard className="w-3.5 h-3.5" /> Accéder au Dashboard Admin
          </Link>
        )}
      </section>

      <section className="pb-24 max-w-4xl mx-auto px-6">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* Profile form */}
          <div className="lg:col-span-3">
            <div className="bg-[#111113] border border-white/10 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-sm font-bold uppercase tracking-widest text-white/40">Mes informations</h2>
                {!editing
                  ? <button data-testid="edit-profile-btn" onClick={() => setEditing(true)}
                      className="flex items-center gap-1.5 text-xs text-[#FF5C00] hover:underline">
                      <Edit2 className="w-3.5 h-3.5" /> Modifier
                    </button>
                  : <button onClick={() => setEditing(false)} className="text-white/30 hover:text-white"><X className="w-4 h-4" /></button>}
              </div>

              {!editing ? (
                <div className="space-y-4">
                  {[
                    { icon: User, label: "Prénom & Nom", value: `${user.prenom} ${user.nom}` },
                    { icon: Mail, label: "Email", value: user.email },
                    { icon: Phone, label: "Téléphone", value: user.telephone || "Non renseigné" },
                    { icon: MapPin, label: "Adresse postale", value: user.adresse_postale || "Non renseignée" },
                  ].map(({ icon: Icon, label, value }) => (
                    <div key={label} className="flex items-start gap-3">
                      <Icon className="w-4 h-4 text-[#FF5C00] mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-white/30">{label}</p>
                        <p className="text-sm text-white">{value}</p>
                      </div>
                    </div>
                  ))}
                  {saveOk && (
                    <div className="flex items-center gap-2 text-[#34C759] text-sm mt-2">
                      <CheckCircle className="w-4 h-4" /> Profil mis à jour !
                    </div>
                  )}
                </div>
              ) : (
                <form onSubmit={handleSave} className="space-y-4" data-testid="profile-form">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-white/40 mb-1.5">Prénom</label>
                      <input type="text" value={form.prenom} onChange={e => setForm(p => ({...p, prenom: e.target.value}))} className={inputCls} />
                    </div>
                    <div>
                      <label className="block text-xs text-white/40 mb-1.5">Nom</label>
                      <input type="text" value={form.nom} onChange={e => setForm(p => ({...p, nom: e.target.value}))} className={inputCls} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-white/40 mb-1.5">Téléphone</label>
                    <input type="tel" value={form.telephone} onChange={e => setForm(p => ({...p, telephone: e.target.value}))} className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-xs text-white/40 mb-1.5">
                      Adresse postale
                      <span className="ml-1 text-white/25">(obligatoire pour envoi postal)</span>
                    </label>
                    <textarea rows={2} value={form.adresse_postale} onChange={e => setForm(p => ({...p, adresse_postale: e.target.value}))}
                      placeholder="9 Rue de Mont-Louis, 75011 Paris"
                      className={`${inputCls} resize-none`} />
                  </div>
                  <button data-testid="save-profile-btn" type="submit" disabled={saving}
                    className="flex items-center gap-2 bg-[#FF5C00] hover:bg-[#E05200] text-white text-sm font-semibold px-6 py-3 rounded-full transition-colors">
                    <Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Sauvegarder"}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Orders */}
          <div className="lg:col-span-2">
            <div className="bg-[#111113] border border-white/10 rounded-2xl p-6">
              <h2 className="text-sm font-bold uppercase tracking-widest text-white/40 mb-6 flex items-center gap-2">
                <ShoppingBag className="w-4 h-4" /> Mes commandes
              </h2>
              {orders.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-10 h-10 text-white/10 mx-auto mb-3" />
                  <p className="text-sm text-white/30">Aucune commande pour l'instant</p>
                  <Link to="/reparation/iphone" className="text-xs text-[#FF5C00] hover:underline mt-2 inline-block">
                    Découvrir nos réparations
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {orders.map((order) => (
                    <div key={order.id} data-testid={`order-${order.id}`}
                      className="p-4 rounded-xl bg-[#0A0A0C] border border-white/5">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                          order.payment_status === "paid"
                            ? "bg-[#34C759]/10 text-[#34C759]"
                            : "bg-white/5 text-white/40"
                        }`}>
                          {order.payment_status === "paid" ? "Payé" : "En attente"}
                        </span>
                        <span className="text-base font-black text-[#FF5C00]">{order.amount_total}€</span>
                      </div>
                      {order.items?.slice(0, 2).map((item, i) => (
                        <p key={i} className="text-xs text-white/50 truncate">{item.name}</p>
                      ))}
                      <p className="text-xs text-white/20 mt-1">
                        {new Date(order.created_at).toLocaleDateString("fr-FR")}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
